import { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import useCostIntervalLookup from './useCostIntervalLookup';
import GqlClientContext from 'contexts/GqlClientContext';
import setIntervalCosts from '../../api/setIntervalCosts';
import ToastContext from 'contexts/ToastContext';
import AnalyticsContext from 'contexts/AnalyticsContext';

const useChanges = (activityCosts: Costs.PersistedCostModel | undefined) => {
  const { client } = useContext(GqlClientContext);
  const { showToast } = useContext(ToastContext);
  const { trackEvent } = useContext(AnalyticsContext);

  const {
    persistableCosts,
    checkIsCpmEstimated,
    intervalCosts,
    refreshCosts,
    getCostValue,
    getCpmValue,
    defaultCostLookup,
    intervals,
    updateCostValue,
    isLoading,
    costsLookup,
    getIsInitialAllGroup,
    hasUnsavedChanges,
  } = useCostIntervalLookup(activityCosts);

  const isValidInterval = useCallback(
    (costInterval: Costs.IntervalCostsInput): boolean => {
      return costInterval.groupCosts.every((g) => {
        return g.costs.every((c) => {
          const userInputValue = getCostValue({
            startDate: costInterval.startDate,
            groupDefinition: g.groupDefinition,
            costFieldName: c.costFieldName,
          });

          return userInputValue !== undefined;
        });
      });
    },
    [getCostValue],
  );

  const isChangedInterval = useCallback(
    (costInterval: Costs.IntervalCostsInput): boolean => {
      return costInterval.groupCosts.some((g) => {
        return g.costs.some((c) => {
          const userInputValue = getCostValue({
            startDate: costInterval.startDate,
            groupDefinition: g.groupDefinition,
            costFieldName: c.costFieldName,
          });

          const persistedInterval = defaultCostLookup[costInterval.startDate];
          const persistedCost = persistedInterval
            ? persistedInterval.groups[g.groupDefinition.groupName].costs[
                c.costFieldName
              ]
            : undefined;

          return userInputValue !== persistedCost;
        });
      });
    },
    [getCostValue, defaultCostLookup],
  );

  // Estimated if (not in persisted) AND (filled) AND (not changed)
  const checkIsIntervalEstimated = useCallback(
    (startDate: string): boolean => {
      if (!intervalCosts || intervalCosts.length === 0) {
        return false;
      }

      const newInterval = persistableCosts.find(
        (i) => i.startDate === startDate,
      );

      return (
        !intervalCosts.find((c) => c.startDate === startDate) &&
        !!newInterval &&
        isValidInterval(newInterval) &&
        !isChangedInterval(newInterval)
      );
    },
    [persistableCosts, intervalCosts, isChangedInterval, isValidInterval],
  );

  const getMutatedCosts = useCallback(() => {
    if (intervalCosts === undefined) {
      return [];
    }

    const persistedCosts = [...intervalCosts];
    const newCosts = persistableCosts;

    const newIntervals = newCosts
      .filter(
        (c1) => !persistedCosts.some((c2) => c2.startDate === c1.startDate),
      )
      .filter(isValidInterval)
      .filter((c) => !checkIsIntervalEstimated(c.startDate));

    const existingIntervals = newCosts.filter(isValidInterval).filter((c1) => {
      const existingInterval = persistedCosts.find(
        (c2) => c2.startDate === c1.startDate,
      );
      if (!existingInterval) {
        return false;
      }

      return !_.isEqual(
        c1.groupCosts.map((g) => g.costs),
        existingInterval.groupCosts.map((g) => g.costs),
      );
    });

    return [...newIntervals, ...existingIntervals].map((interval) => ({
      ...interval,
      groupCosts: interval.groupCosts.map((groupCost) => ({
        groupDefinition: groupCost.groupDefinition,
        costs: groupCost.costs,
      })),
    }));
  }, [
    persistableCosts,
    intervalCosts,
    checkIsIntervalEstimated,
    isValidInterval,
  ]);

  const saveChanges = useCallback(async () => {
    if (activityCosts === undefined) {
      const err = new Error();
      err.name = 'Cost model not found';
      throw err;
    }

    await setIntervalCosts({
      costModelId: activityCosts.id,
      intervalCosts: getMutatedCosts(),
      client,
    });
    await refreshCosts();
    showToast('Changes saved');

    if (activityCosts.isDraft) {
      trackEvent('Costs - Draft Saved', {
        costModel: activityCosts.modelType,
      });
    } else {
      trackEvent('Costs - Updated Costs', {
        costModel: activityCosts.modelType,
      });
    }
  }, [
    activityCosts,
    client,
    getMutatedCosts,
    refreshCosts,
    showToast,
    trackEvent,
  ]);

  const getIsValid = useCallback(() => {
    if (intervals.length === 0) {
      return false;
    }

    const mutatedCosts = getMutatedCosts();
    return mutatedCosts.length !== 0 && mutatedCosts.every(isValidInterval);
  }, [getMutatedCosts, intervals.length, isValidInterval]);

  const getInvalidIntervals = useCallback(() => {
    if (intervals.length === 0) {
      return [];
    }

    const newCosts = persistableCosts;
    const changedCosts = newCosts.filter(isChangedInterval);

    return changedCosts.filter((changedCosts) => {
      return !isValidInterval(changedCosts);
    });
  }, [persistableCosts, intervals.length, isChangedInterval, isValidInterval]);

  const [isValid, setIsValid] = useState<boolean>(getIsValid());
  const [savingError, setSavingError] = useState<{
    hasError: boolean;
    invalidIntervals: Costs.IntervalCostsInput[];
  }>({
    hasError: false,
    invalidIntervals: [],
  });

  useEffect(() => {
    if (!savingError.hasError) {
      return;
    }

    const newInvalidIntervals = getInvalidIntervals();
    if (newInvalidIntervals.length === 0) {
      setSavingError({ hasError: false, invalidIntervals: [] });
    } else {
      setSavingError({ hasError: true, invalidIntervals: newInvalidIntervals });
    }
  }, [getInvalidIntervals, savingError.hasError]);

  useEffect(() => {
    setIsValid(getIsValid());
  }, [getIsValid]);

  return {
    saveChanges,
    isValid,
    getInvalidIntervals,
    savingError,
    setSavingError,
    hasUnsavedChanges,
    getCostValue,
    getCpmValue,
    updateCostValue,
    persistableCosts,
    intervals,
    isLoading,
    checkIsIntervalEstimated,
    getMutatedCosts,
    checkIsCpmEstimated,
    costsLookup,
    getIsInitialAllGroup,
  };
};

export default useChanges;
